import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Search } from "lucide-react";

function fmtTs(ts) {
  if (!ts) return "—";
  return new Date(ts * 1000).toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

function toKiwiDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

function stopsLabel(route) {
  const n = Math.max(0, (route?.length || 1) - 1);
  return n === 0 ? "NONSTOP" : n === 1 ? "1 STOP" : `${n} STOPS`;
}

function stopsValue(route) {
  const n = Math.max(0, (route?.length || 1) - 1);
  return n === 0 ? "nonstop" : n === 1 ? "1 stop" : "2+ stops";
}

export default function AdminDealFinder() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ fly_from: "", fly_to: "", date_from: "", date_to: "", price_to: "" });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const search = async (e) => {
    e.preventDefault();
    if (!form.fly_from.trim()) { toast.error("ORIGIN AIRPORT CODE REQUIRED"); return; }
    if (!form.date_from || !form.date_to) { toast.error("DATE RANGE REQUIRED"); return; }
    setLoading(true);
    setResults([]);
    setSearched(false);
    try {
      const payload = {
        fly_from: form.fly_from.trim().toUpperCase(),
        date_from: toKiwiDate(form.date_from),
        date_to: toKiwiDate(form.date_to),
        ...(form.fly_to.trim() ? { fly_to: form.fly_to.trim().toUpperCase() } : {}),
        ...(form.price_to ? { price_to: parseInt(form.price_to, 10) } : {}),
      };
      const { data } = await api.post("/admin/deal-finder", payload);
      setResults(data.data || []);
      setSearched(true);
      if (!data.data?.length) toast.error("NO RESULTS — try different parameters");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "SEARCH FAILED");
    } finally {
      setLoading(false);
    }
  };

  const addAsDeal = (flight) => {
    navigate("/admin/new", {
      state: {
        prefill: {
          from_city: flight.cityFrom || "",
          from_country: flight.countryFrom?.name || "",
          from_airport: flight.flyFrom || "",
          to_city: flight.cityTo || "",
          to_country: flight.countryTo?.name || "",
          to_airport: flight.flyTo || "",
          price_eur: Math.round(flight.price),
          airline: (flight.airlines || [])[0] || "",
          deal_url: flight.deep_link || "",
          deal_type: "flight",
          stops: stopsValue(flight.route),
        },
      },
    });
  };

  return (
    <div className="max-w-4xl">
      <header className="mb-8">
        <p className="eyebrow eyebrow-red">// DEAL FINDER</p>
        <h1 className="text-2xl font-mono tracking-tight">Find Cheap Flights</h1>
        <p className="text-sm text-[#666] font-mono mt-1">
          Powered by Kiwi.com · results sorted by price · click ADD AS DEAL to pre-fill the deal form
        </p>
      </header>

      {/* Search form */}
      <form onSubmit={search} className="border border-[#2A2A2A] bg-[#0E0E0E] p-6 space-y-4 mb-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="ed-label">ORIGIN AIRPORT <span className="text-[#CC1A1A]">*</span></label>
            <input
              className="ed-input uppercase"
              placeholder="AMS"
              maxLength={3}
              value={form.fly_from}
              onChange={(e) => set("fly_from", e.target.value)}
              data-testid="finder-fly-from"
            />
          </div>
          <div>
            <label className="ed-label">DESTINATION AIRPORT <span className="text-[#555]">(optional)</span></label>
            <input
              className="ed-input uppercase"
              placeholder="any"
              maxLength={3}
              value={form.fly_to}
              onChange={(e) => set("fly_to", e.target.value)}
              data-testid="finder-fly-to"
            />
          </div>
          <div>
            <label className="ed-label">DEPART FROM <span className="text-[#CC1A1A]">*</span></label>
            <input
              className="ed-input"
              type="date"
              value={form.date_from}
              onChange={(e) => set("date_from", e.target.value)}
              data-testid="finder-date-from"
            />
          </div>
          <div>
            <label className="ed-label">DEPART TO <span className="text-[#CC1A1A]">*</span></label>
            <input
              className="ed-input"
              type="date"
              value={form.date_to}
              onChange={(e) => set("date_to", e.target.value)}
              data-testid="finder-date-to"
            />
          </div>
          <div>
            <label className="ed-label">MAX PRICE (EUR)</label>
            <input
              className="ed-input"
              type="number"
              min="0"
              placeholder="500"
              value={form.price_to}
              onChange={(e) => set("price_to", e.target.value)}
              data-testid="finder-price-to"
            />
          </div>
        </div>
        <button
          type="submit"
          disabled={loading}
          className="btn-rd flex items-center gap-2"
          data-testid="finder-search-btn"
        >
          <Search className="w-4 h-4" />
          {loading ? "SCANNING…" : "SEARCH FLIGHTS"}
        </button>
      </form>

      {/* Empty state */}
      {searched && results.length === 0 && (
        <p className="text-[#666] font-mono text-sm">No flights found. Try a wider date range or higher max price.</p>
      )}

      {/* Results */}
      {results.length > 0 && (
        <div className="space-y-3">
          <p className="text-[10px] font-mono tracking-[0.24em] uppercase text-[#555] mb-4">
            // {results.length} RESULT{results.length !== 1 ? "S" : ""} — SORTED BY PRICE
          </p>
          {results.map((flight, idx) => (
            <div
              key={flight.id || idx}
              className="border border-[#2A2A2A] bg-[#0E0E0E] p-4 flex flex-col sm:flex-row sm:items-center gap-3"
              data-testid={`finder-result-${idx}`}
            >
              <div className="flex-1 min-w-0">
                <p className="font-mono text-white text-sm tracking-wide">
                  {flight.flyFrom} <span className="text-[#CC1A1A]">→</span> {flight.flyTo}
                  <span className="ml-3 text-[#9A8F83] text-xs font-normal">
                    {flight.cityFrom} → {flight.cityTo}
                  </span>
                </p>
                <p className="text-[11px] font-mono text-[#555] mt-1 space-x-3">
                  <span>{fmtTs(flight.dTime)}</span>
                  <span>·</span>
                  <span>{(flight.airlines || []).join(", ") || "—"}</span>
                  <span>·</span>
                  <span>{stopsLabel(flight.route)}</span>
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="font-mono text-[#CC1A1A] text-xl font-bold">
                  €{Math.round(flight.price)}
                </span>
                <button
                  type="button"
                  onClick={() => addAsDeal(flight)}
                  className="btn-rd !py-2 !px-3 !text-[0.65rem] whitespace-nowrap"
                  data-testid={`finder-add-${idx}`}
                >
                  ADD AS DEAL →
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Config hint */}
      {!loading && (
        <p className="text-[10px] font-mono text-[#333] mt-10">
          Requires KIWI_API_KEY in backend/.env — get a free key at tequila.kiwi.com
        </p>
      )}
    </div>
  );
}
